import React, { useEffect, useRef, useState } from "react"
import { Box, Button, Select, Text } from "@chakra-ui/react"

// ─── Shared Doctor Type ───────────────────────────────────────────────────────

export interface RegistryDoctor {
  _id: string
  name: string
  email: string
  phone?: string
  mobileNo?: { countryCode: number; number: number }
  specialization: string
  qualification: string
  registrationNumber?: string
  registration_number?: string
  doctor_type?: string
  status: string
  is_verified: boolean
  createdAt: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const getInitials = (name: string) =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("")

export const getPhone = (doc: RegistryDoctor) =>
  doc.phone ?? (doc.mobileNo ? `+${doc.mobileNo.countryCode}${doc.mobileNo.number}` : "—")

export const getRegNumber = (doc: RegistryDoctor) =>
  doc.registration_number ?? doc.registrationNumber ?? "—"

// ─── Label & SectionHeader ────────────────────────────────────────────────────

export const Label: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
  <Text fontSize="sm" fontWeight="medium" mb={1}>
    {children}{required && <Text as="span" color="red.400"> *</Text>}
  </Text>
)

export const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text fontWeight="bold" fontSize="md" borderLeft="4px solid" borderColor="green.400" pl={3} mb={2}>
    {children}
  </Text>
)

// ─── MultiSelectDropdown ──────────────────────────────────────────────────────

interface MultiSelectDropdownProps {
  options: string[]
  value: string[]
  onChange: (val: string[]) => void
  placeholder?: string
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({ options, value, onChange, placeholder = "Select..." }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const toggle = (opt: string) =>
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt])

  return (
    <Box ref={ref} position="relative">
      <Box
        as="button" type="button" onClick={() => setOpen((o) => !o)}
        w="full" minH="32px" px={3} py={1}
        border="1px solid" borderColor="gray.200" borderRadius="md"
        bg="white" textAlign="left" fontSize="sm"
        display="flex" alignItems="center" flexWrap="wrap" gap={1}
        _hover={{ borderColor: "gray.300" }}
      >
        {value.length === 0 ? (
          <Text color="gray.400" fontSize="sm">{placeholder}</Text>
        ) : value.map((v) => (
          <Box key={v} display="inline-flex" alignItems="center" gap={1}
            bg="blue.50" color="blue.700" px={2} py={0.5} borderRadius="full" fontSize="xs" fontWeight="medium">
            {v}
            <Box as="span" cursor="pointer" fontWeight="bold" lineHeight={1}
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); toggle(v) }}>×</Box>
          </Box>
        ))}
        <Box ml="auto" color="gray.400" fontSize="xs" flexShrink={0}>{open ? "▲" : "▼"}</Box>
      </Box>

      {open && (
        <Box position="absolute" zIndex={10} top="calc(100% + 4px)" left={0} right={0}
          bg="white" border="1px solid" borderColor="gray.200" borderRadius="md"
          shadow="md" maxH="220px" overflowY="auto">
          {options.map((opt) => (
            <Box key={opt} px={3} py={2} cursor="pointer" fontSize="sm"
              display="flex" alignItems="center" gap={2}
              bg={value.includes(opt) ? "blue.50" : "white"}
              color={value.includes(opt) ? "blue.700" : "gray.700"}
              _hover={{ bg: value.includes(opt) ? "blue.100" : "gray.50" }}
              onClick={() => toggle(opt)}>
              <Box w={4} h={4} borderRadius="sm" border="1px solid"
                borderColor={value.includes(opt) ? "blue.500" : "gray.300"}
                bg={value.includes(opt) ? "blue.500" : "white"}
                display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                {value.includes(opt) && <Text fontSize="10px" color="white" lineHeight={1}>✓</Text>}
              </Box>
              {opt}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

// ─── DoctorSelectorBanner ────────────────────────────────────────────────────

interface DoctorSelectorBannerProps {
  doctors: RegistryDoctor[]
  selected: RegistryDoctor | null
  onSelect: (doc: RegistryDoctor) => void
  onGoToRegistry: () => void
  onRefresh: () => void
  refreshing?: boolean
}

export const DoctorSelectorBanner: React.FC<DoctorSelectorBannerProps> = ({ doctors, selected, onSelect, onGoToRegistry, onRefresh, refreshing }) => (
  <Box mb={5} p={4} bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md"
    display="flex" alignItems="center" gap={4} flexWrap="wrap">
    <Text fontSize="sm" fontWeight="semibold" color="blue.700" flexShrink={0}>Doctor:</Text>
    <Select size="sm" w="320px" placeholder="— Select a doctor —"
      value={selected?._id ?? ""}
      onChange={(e) => { const doc = doctors.find((d) => d._id === e.target.value); if (doc) onSelect(doc) }}>
      {doctors.map((d) => (
        <option key={d._id} value={d._id}>{d.name} — {d.specialization}</option>
      ))}
    </Select>
    <Button size="sm" variant="ghost" colorScheme="blue" onClick={onGoToRegistry}>← Go to Registry</Button>
    <Box ml="auto">
      <Button size="sm" variant="outline" colorScheme="blue" onClick={onRefresh} isLoading={refreshing}>Refresh</Button>
    </Box>
  </Box>
)
